/**
 * Pure helper that diffs two form-version snapshots and projects the user's
 * current field_values onto the dropped fields so the upgrade dialog can show
 * "you entered X for this field — it will be lost if you upgrade."
 *
 * No React, no Supabase — testable in isolation.
 */

export type DroppedFieldReason = 'removed' | 'type_changed'

export interface DroppedFieldDiff {
  field_key: string
  label_fa: string
  reason: DroppedFieldReason
  /** Original field type in the OLD pinned version. */
  old_type?: string
  /** New field type in the CURRENT version (only when reason='type_changed'). */
  new_type?: string
  /**
   * Stringified representation of the value the user had entered for this
   * field on this submission. Empty string when never filled.
   */
  current_value: string
}

interface RawField {
  field_key?: unknown
  field_type?: unknown
  label?: unknown
}

function readLabel(raw: unknown): string {
  if (typeof raw === 'string') return raw
  if (raw && typeof raw === 'object') {
    const fa = (raw as Record<string, unknown>).fa
    if (typeof fa === 'string') return fa
  }
  return ''
}

function stringifyValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return ''
  if (Array.isArray(v)) {
    if (v.length === 0) return ''
    return v.map((x) => stringifyValue(x)).filter(Boolean).join('، ')
  }
  if (typeof v === 'object') {
    // Likely an uploaded file ref or a localized object — show a sensible label
    const r = v as Record<string, unknown>
    if (typeof r.originalName === 'string') return r.originalName
    if (typeof r.fa === 'string') return r.fa
    return JSON.stringify(v)
  }
  if (typeof v === 'boolean') return v ? 'بله' : 'خیر'
  return String(v)
}

/**
 * Compute the dropped-field diff between two form-version definition
 * snapshots, attaching the user's previously-entered value for each.
 *
 * A field is "dropped" when:
 *   - It exists in OLD but not in NEW (`reason='removed'`)
 *   - It exists in both but `field_type` differs (`reason='type_changed'`)
 *
 * Renamed labels and reordered fields are NOT dropped.
 */
export function computeVersionDiff(
  oldFields: unknown,
  newFields: unknown,
  currentValues: Record<string, unknown>,
): DroppedFieldDiff[] {
  const oldArr: RawField[] = Array.isArray(oldFields) ? (oldFields as RawField[]) : []
  const newArr: RawField[] = Array.isArray(newFields) ? (newFields as RawField[]) : []
  const newByKey = new Map<string, RawField>()
  for (const f of newArr) {
    if (typeof f.field_key === 'string') newByKey.set(f.field_key, f)
  }

  const out: DroppedFieldDiff[] = []
  for (const f of oldArr) {
    if (typeof f.field_key !== 'string') continue
    const key = f.field_key
    const oldType = typeof f.field_type === 'string' ? f.field_type : ''
    const labelFa = readLabel(f.label) || key
    const value = stringifyValue(currentValues?.[key])

    const match = newByKey.get(key)
    if (!match) {
      out.push({
        field_key: key,
        label_fa: labelFa,
        reason: 'removed',
        old_type: oldType,
        current_value: value,
      })
    } else if (typeof match.field_type === 'string' && match.field_type !== oldType) {
      out.push({
        field_key: key,
        label_fa: labelFa,
        reason: 'type_changed',
        old_type: oldType,
        new_type: match.field_type,
        current_value: value,
      })
    }
  }
  return out
}
